import { Check, Loader2, Table2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { BaseToolBadgeProps } from './types';

export const UpdateTableRowBadge = ({
  isLoading = false,
  isDone = false,
  isError = false,
  args = {},
}: BaseToolBadgeProps) => {
  // Determine the current state
  let state = 'default';
  if (isError) {
    state = 'error';
  } else if (isLoading) {
    state = 'loading';
  } else if (isDone) {
    state = 'done';
  }

  // Get all the table row data from args
  const rowData = Object.entries(args).filter(
    ([key]) => !['config', 'thread_id'].includes(key),
  );

  const headerText =
    state === 'loading'
      ? 'Updating table row…'
      : state === 'error'
        ? 'Error updating table row'
        : 'Table row updated';

  return (
    <Badge
      variant={state === 'done' ? 'outline' : 'secondary'}
      className={cn(
        'flex items-start gap-2 px-3 py-2 text-xs font-medium max-w-2xl',
        state === 'done' &&
          'bg-green-50 text-green-700 hover:bg-green-50 hover:text-green-700',
        state === 'error' &&
          'bg-red-50 text-red-700 hover:bg-red-50 hover:text-red-700',
      )}
    >
      <Table2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div className="flex flex-col gap-2 min-w-0">
        <span className="flex items-center gap-1.5 font-medium">
          {headerText}
          {state === 'loading' && <Loader2 className="h-3 w-3 animate-spin" />}
          {state === 'done' && <Check className="h-3 w-3" />}
          {state === 'error' && <X className="h-3 w-3" />}
        </span>

        {rowData.length > 0 && (
          <div className="grid gap-1.5 text-xs">
            {rowData.map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <span className="font-medium text-slate-600 whitespace-nowrap">
                  {formatColumnName(key)}:
                </span>
                <span className="text-slate-700 break-words">
                  {String(value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Badge>
  );
};

/**
 * Convert snake_case column IDs to readable labels
 */
function formatColumnName(columnId: string): string {
  return columnId
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
